<template>
    <div :class="['contact-item', { 'active': isActive }]" @click="$emit('click')">
        <div class="contact-main-info">
            <div class="contact-avatar-wrapper position-relative">
                <div class="contact-avatar">
                    <img v-if="contact.picture" :src="storageUrl + contact.picture"
                        :alt="contact.username || contact.firstname" />
                    <span v-else class="avatar-initial">
                        {{ (contact.username || contact.firstname).charAt(0).toUpperCase() }}
                    </span>
                </div>

                <div v-if="contact.verified" class="verified-badge">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd" />
                    </svg>
                </div>

                <span class="status-bubble" :class="contact.is_online ? 'online' : 'offline'"></span>
            </div>

            <div class="contact-info">
                <div class="contact-header">
                    <p class="contact-name">
                        {{ contact.username || contact.firstname }}
                        <svg v-if="contact.verified" class="verified-icon" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clip-rule="evenodd" />
                        </svg>
                    </p>
                    <span class="contact-time">{{ contact.last_message_time | moment }}</span>
                </div>

                <p class="contact-last-message" :class="{ 'offer-message': isBoldMessage(contact.last_message) }">
                    <!-- <span v-if="contact.product.user_id === user.id">
                        {{ $t("general.you") }}:
                    </span> -->

                    {{ formatMessage(contact.last_message) }}
                </p>


                <div v-if="products.length" class="last-product-thumbnails d-flex gap-2">
                    <div v-for="(product, idx) in products" :key="product.id || idx"
                        class="product-thumb position-relative">
                        <img :src="storageUrl + (product.pictures && product.pictures.length ? product.pictures[0] : '')"
                            :alt="product.name || ''" />

                        <!-- per-product badges -->
                        <span v-if="product.deleted_at"
                            class="badge badge-sm badge-danger thumb-badge d-inline-flex align-items-center py-0">
                            <i class="mdi mdi-close-circle"></i>
                        </span>
                        <span v-else-if="product.has_been_sold === 1"
                            class="badge badge-sm badge-success thumb-badge d-inline-flex align-items-center py-0">
                            <i class="mdi mdi-cart-off"></i>
                        </span>
                    </div>
                </div>
            </div>
            <div v-if="contact.unread > 0" class="unread-message-badge">
                {{ contact.unread }}
            </div>
        </div>
    </div>
</template>

<script>
import config from "@/config";

export default {
    name: "ContactItem",
    props: {
        contact: {
            type: Object,
            required: true
        },
        isActive: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            storageUrl: config.storageUrl
        };
    },
    computed: {
        user() {
            return this.$store.state.user;
        }
        ,
        products() {
            // Support multiple product sources:
            // - offer.offer_products -> array of { product }
            // - contact.product -> single product object
            const offer=this.contact&&this.contact.offer;
            if (offer&&Array.isArray(offer.offer_products)&&offer.offer_products.length) {
                return offer.offer_products
                    .map(op => op&&op.product)
                    .filter(p => p);
            }

            if (this.contact&&this.contact.product) {
                return [this.contact.product];
            }

            return [];
        }
    },
    methods: {
        isBoldMessage(message) {
            return (
                typeof message==='string'&&
                message.startsWith('**')&&
                message.endsWith('**')
            );
        },
        formatMessage(message) {
            if (this.isBoldMessage(message)) {
                return message.slice(2, -2); // remove ** **
            }
            return message!=='*'? message:'';
        }
    }

};
</script>

<style scoped>
.contact-item {
    padding: 12px;
    cursor: pointer;
    border-bottom: 1px solid var(--gray-100);
    transition: background 0.2s;
    position: relative;
}

.contact-item:hover {
    background: var(--gray-50);
}

.contact-item.active {
    background: var(--gray-100);
}

.contact-main-info {
    display: flex;
    align-items: center;
}

.contact-avatar-wrapper {
    position: relative;
    margin-right: 12px;
}

.contact-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--color-nz);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.contact-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-initial {
    color: white;
    font-size: 18px;
    font-weight: 600;
}

.verified-badge {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 16px;
    height: 16px;
    background: var(--success-400);
    border-radius: 50%;
    border: 2px solid white;
    display: flex;
    align-items: center;
    justify-content: center;
}

.verified-badge svg {
    width: 8px;
    height: 8px;
    color: white;
}

.status-bubble {
    position: absolute;
    bottom: 2px;
    right: 0px;
    width: 12px;
    height: 12px;
    border: 2px solid white;
    border-radius: 50%;
}

.status-bubble.online {
    background-color: var(--success-200);
}

.status-bubble.offline {
    background-color: var(--gray-400);
}

.contact-info {
    flex: 1;
    min-width: 0;
}

.contact-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
}

.contact-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--gray-900);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 4px;
}

.verified-icon {
    width: 16px;
    height: 16px;
    color: var(--success-400);
    flex-shrink: 0;
}

.contact-time {
    font-size: 12px;
    color: var(--gray-500);
    flex-shrink: 0;
}

.contact-last-message {
    font-size: 14px;
    color: var(--gray-500);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.last-product-thumbnail,
.last-product-thumbnails {
    margin-top: 2px;
}

.last-product-thumbnails .product-thumb,
.last-product-thumbnail {
    /* container for each thumb (keeps compatibility) */
    position: relative;
}

.last-product-thumbnails .product-thumb img,
.last-product-thumbnail img {
    width: 30px;
    height: 30px;
    object-fit: cover;
    border-radius: 6px;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.15);
}

.product-thumb .thumb-badge {
    position: absolute;
    bottom: -6px;
    right: -6px;
    padding: 0.125rem 0.25rem;
    font-size: 10px;
    line-height: 1;
    border-radius: 10px;
}

.unread-message-badge {
    background: var(--color-nz);
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
}

.offer-message {
    display: inline-block;
    padding: 0px 8px;
    background-color: var(--primary-light);
    color:  var(--color-nz);
    font-weight: 600;
    border-radius: 6px;
    border: 1px solid  var(--gray-100);
    font-size: 0.9rem;
}
</style>